// practice cards page
import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import skip from './assets/skip.png';
import forgot from './assets/forgot.png';
import partially from './assets/partially.png';
import efforts from './assets/efforts.png';
import convenient from './assets/convenient.png';
import { useParams, useNavigate } from 'react-router-dom';
import { StaticMathField } from 'react-mathquill';

// Styled Components with Modern Design
const FlipCard = styled(Card)(({ isExiting }) => ({
  width: '90%',
  maxWidth: 900,
  minHeight: 550,
  margin: 'auto',
  position: 'relative',
  backgroundColor: 'transparent',
  borderRadius: '24px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  animation: isExiting ? 'slideOutLeft 0.5s ease-out' : 'slideInRight 0.5s ease-out',
  '@keyframes slideInRight': {
    from: { transform: 'translateX(100%)', opacity: 0 },
    to: { transform: 'translateX(0)', opacity: 1 },
  },
  '@keyframes slideOutLeft': {
    from: { transform: 'translateX(0)', opacity: 1 },
    to: { transform: 'translateX(-100%)', opacity: 0 },
  },
}));

const GradientCardWrapper = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
  borderRadius: '24px',
  padding: '24px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
}));

const FlipCardContent = styled(CardContent)(() => ({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '40px',
  minHeight: '500px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.05)',
}));

const StyledButton = styled(Button)(() => ({
  background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
  color: 'white',
  width: '100%',
  minHeight: '60px',
  fontSize: '1.25rem',
  fontWeight: 'bold',
  borderRadius: '30px',
  textTransform: 'none',
  boxShadow: '0 8px 20px rgba(102,126,234,0.4)',
  transition: 'all 0.3s',
  '&:hover': {
    background: 'linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 28px rgba(102,126,234,0.5)',
  },
}));

const ImageButton = styled(IconButton)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

const Image = styled('img')(() => ({
  width: '50px',
  height: '50px',
  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
}));

const QuestionBadge = styled(Box)(() => ({
  backgroundColor: 'rgba(255,255,255,0.2)',
  color: '#fff',
  padding: '8px 20px',
  borderRadius: 20,
  fontWeight: 'bold',
  fontSize: '0.9rem',
  backdropFilter: 'blur(10px)',
  display: 'inline-block',
}));

const AnswerBox = styled(Box)(() => ({
  marginTop: '24px',
  padding: '24px',
  backgroundColor: 'rgba(30, 139, 175, 0.1)',
  borderRadius: '12px',
  borderLeft: '4px solid #1e8bafff',
}));

export default function CardsDisplay() {
  const [flashcards, setFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [revealedLabelIndices, setRevealedLabelIndices] = useState([]);
  const { subfolder_id } = useParams();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_BASE_URL;
const [isExiting, setIsExiting] = useState(false);

useEffect(() => {
  window.scrollTo(0, 0);
}, [currentIndex]);


const userRole = sessionStorage.getItem('userRole');

  useEffect(() => {
    const fetchFlashcards = async () => {
      try {
        const singleLineResponse = await fetch(`${apiUrl}/subfolder/${subfolder_id}/sub/`);
        const fillInTheBlanksResponse = await fetch(`${apiUrl}/subfolder/${subfolder_id}/fib/`);
        const mcqResponse = await fetch(`${apiUrl}/subfolder/${subfolder_id}/mcq/`);
        const trueFalseResponse = await fetch(`${apiUrl}/subfolder/${subfolder_id}/truefalse/`);
        const imageResponse = await fetch(`${apiUrl}/subfolder/${subfolder_id}/${userRole === 'admin' ? 'uploadimage/' : 'uploaded_images/'}`);


        const singleLineData = await singleLineResponse.json();
        const fillInTheBlanksData = await fillInTheBlanksResponse.json();
        const mcqData = await mcqResponse.json();
        const trueFalseData = await trueFalseResponse.json();
        const imageData = await imageResponse.json();

        const processedMcqData = mcqData.map(mcq => ({
          ...mcq,
          answers: mcq.answers.find(answer => answer.is_correct)?.answer_text || '',
        }));

        const allFlashcards = [
          ...singleLineData,
          ...fillInTheBlanksData,
          ...processedMcqData,
          ...trueFalseData,
          ...imageData.map(img => ({
            ...img,
            canvas_width: img.canvas_width || null,
            canvas_height: img.canvas_height || null,
          })),
        ];

        setFlashcards(allFlashcards);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchFlashcards();
  }, [subfolder_id, apiUrl]);

  const handleFlip = () => {
    setShowAnswer(!showAnswer);
  };

  const handleNext = async (feedback) => {
  setIsExiting(true);
  
  setTimeout(async () => {
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
    setShowAnswer(false);
    setRevealedLabelIndices([]);
    setIsExiting(false);
  }, 500); // Match animation duration
  
  try {
    const questionId = flashcards[currentIndex].id;
    const question_type = flashcards[currentIndex].question_type;
    await fetch(`${apiUrl}/subfolder/${subfolder_id}/feedbacks/${question_type.toLowerCase()}/${questionId}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ feedback }),
    });
  } catch (error) {
    console.error('Error sending feedback:', error);
  }
};

  const handleClose = () => {
    setShowAnswer(false);
    navigate(-1);
  };

  const renderQuestionContent = () => {
    const currentFlashcard = flashcards[currentIndex];
    if (!currentFlashcard) return <Typography>Loading question...</Typography>;

    switch (currentFlashcard.question_type) {
      case 'fillups':
        return <StaticMathField>{currentFlashcard.fillups_question}</StaticMathField>;
      case 'mcq':
        return <StaticMathField>{currentFlashcard.mcq_question}</StaticMathField>;
      case 'true_false':
        return <Typography variant="h5" sx={{ lineHeight: 1.6 }}>{currentFlashcard.true_false_statement}</Typography>;
      case 'IMAGE':
        return <ImageQuestionDisplay flashcard={currentFlashcard} />;
      default:
        return <StaticMathField>{currentFlashcard.statement}</StaticMathField>;
    }
  };

  const ImageQuestionDisplay = ({ flashcard }) => {
    const canvasRef = useRef(null);
    const [baseImageLoaded, setBaseImageLoaded] = useState(false);
    
    useEffect(() => {
      if (!canvasRef.current || !flashcard.file_url || !flashcard.labels || baseImageLoaded) return;
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        let canvasWidth, canvasHeight;
        
        if (flashcard.canvas_width && flashcard.canvas_height) {
          canvasWidth = flashcard.canvas_width;
          canvasHeight = flashcard.canvas_height;
        } else {
          const maxWidth = 800;
          const aspectRatio = img.width / img.height;
          canvasWidth = Math.min(maxWidth, img.width);
          canvasHeight = canvasWidth / aspectRatio;
        }
        
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
        
        const colors = [
          'rgba(255, 0, 0, 1)', 'rgba(0, 0, 255, 1)', 'rgba(0, 255, 0, 1)',
          'rgba(255, 255, 0, 1)', 'rgba(255, 0, 255, 1)', 'rgba(0, 255, 255, 1)',
          'rgba(255, 128, 0, 1)', 'rgba(128, 0, 255, 1)',
        ];
        
        flashcard.labels.forEach((label, index) => {
          ctx.fillStyle = colors[index % colors.length];
          ctx.fillRect(label.x, label.y, label.width, label.height);
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 24px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label.id, label.x + label.width / 2, label.y + label.height / 2);
        });
        
        setBaseImageLoaded(true);
      };
      
      img.src = flashcard.file_url;
    }, [flashcard.file_url, flashcard.labels, baseImageLoaded, flashcard.canvas_width, flashcard.canvas_height]);
    
    useEffect(() => {
      if (!baseImageLoaded || !canvasRef.current) return;
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      const colors = [
        'rgba(255, 0, 0, 1)', 'rgba(0, 0, 255, 1)', 'rgba(0, 255, 0, 1)',
        'rgba(255, 255, 0, 1)', 'rgba(255, 0, 255, 1)', 'rgba(0, 255, 255, 1)',
        'rgba(255, 128, 0, 1)', 'rgba(128, 0, 255, 1)',
      ];
      
      flashcard.labels.forEach((label, index) => {
        if (revealedLabelIndices.includes(index)) {
          ctx.fillStyle = colors[index % colors.length];
          ctx.fillRect(label.x, label.y, label.width, label.height);
          
          const textX = label.x + label.width / 2;
          const textY = label.y + label.height / 2;
          const padding = 4;
          
          ctx.font = 'bold 16px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          const text = `${label.id}. ${label.answer}`;
          const textMetrics = ctx.measureText(text);
          const textWidth = textMetrics.width;
          const textHeight = 20;
          
          ctx.fillStyle = 'white';
          ctx.fillRect(
            textX - textWidth / 2 - padding,
            textY - textHeight / 2 - padding,
            textWidth + padding * 2,
            textHeight + padding * 2
          );
          
          ctx.fillStyle = '#000000';
          ctx.fillText(text, textX, textY);
        }
      });
    }, [revealedLabelIndices, baseImageLoaded, flashcard.labels]);
    
    return (
      <Box sx={{ textAlign: 'center' }}>
        <canvas
          ref={canvasRef}
          style={{
            maxWidth: '100%',
            border: '2px solid #e0e0e0',
            borderRadius: '12px',
            display: 'block',
            margin: '0 auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        />
        <Typography variant="body2" sx={{ mt: 2, color: '#666', fontStyle: 'italic' }}>
          {flashcard.statement}
        </Typography>
        {!baseImageLoaded && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="caption" sx={{ ml: 1 }}>Loading image...</Typography>
          </Box>
        )}
      </Box>
    );
  };

  const currentFlashcard = flashcards[currentIndex];

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: 4,
         overflow: "hidden",
      }}
    >
<FlipCard key={currentIndex} isExiting={isExiting}>
          <GradientCardWrapper>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <QuestionBadge>
              Question {currentIndex + 1} of {flashcards.length}
            </QuestionBadge>
            <IconButton onClick={handleClose} sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <FlipCardContent>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" component="div" sx={{ fontWeight: 600, mb: 3, textAlign: 'center', lineHeight: 1.5 }}>
                {currentFlashcard && currentFlashcard.question_type === 'IMAGE' ? (
                  <Box>
                    <ImageQuestionDisplay flashcard={currentFlashcard} />
                    
                    {revealedLabelIndices.length < currentFlashcard.labels.length && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <StyledButton
                          onClick={() => {
                            const nextIndex = revealedLabelIndices.length;
                            setRevealedLabelIndices(prev => [...prev, nextIndex]);
                          }}
                          sx={{ maxWidth: 300 }}
                        >
                          Reveal Next ({revealedLabelIndices.length + 1}/{currentFlashcard.labels.length})
                        </StyledButton>
                      </Box>
                    )}
                    
                    {revealedLabelIndices.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        {currentFlashcard.labels.map((label, index) => (
                          revealedLabelIndices.includes(index) && (
                            <Typography
                              key={index}
                              variant="body1"
                              sx={{
                                backgroundColor: '#e3f2fd',
                                color: '#000',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                display: 'block',
                                margin: '8px 0',
                                fontWeight: 500,
                              }}
                            >
                              <strong>{label.id}:</strong> {label.answer}
                              {label.explanation && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#555', mt: 1, pl: 2 }}>
                                  💡 {label.explanation}
                                </Typography>
                              )}
                            </Typography>
                          )
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : (
                  renderQuestionContent()
                )}
              </Typography>
              
              {showAnswer && currentFlashcard && currentFlashcard.question_type !== 'IMAGE' && (
                <AnswerBox>
                  <Typography variant="h6" sx={{ color: '#1e8bafff', mb: 1, fontWeight: 'bold' }}>
                    ✓ Answer:
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 500 }}>
                    <StaticMathField>{flashcards[currentIndex].answers}</StaticMathField>
                  </Typography>
                </AnswerBox>
              )}
            </Box>

            {!showAnswer && currentFlashcard && currentFlashcard.question_type !== 'IMAGE' && (
              <Box sx={{ mt: 3 }}>
                <StyledButton onClick={handleFlip} startIcon={<VisibilityIcon />}>
                  👁️ Show Answer
                </StyledButton>
              </Box>
            )}
            
            {currentFlashcard && (
              ((currentFlashcard.question_type === 'IMAGE' && revealedLabelIndices.length > 0) ||
               (currentFlashcard.question_type !== 'IMAGE' && showAnswer)) && (
                <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" align="center" sx={{ mb: 2, color: '#666', fontWeight: 600 }}>
                    How well did you know this?
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 1 }}>
                    <ImageButton onClick={() => handleNext('skip')}>
                      <Image src={skip} alt="skip" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Skip</Typography>
                    </ImageButton>
                    <ImageButton onClick={() => handleNext('forgot')}>
                      <Image src={forgot} alt="forgot" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Forgot</Typography>
                    </ImageButton>
                    <ImageButton onClick={() => handleNext('partially recalled')}>
                      <Image src={partially} alt="partially" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Partial</Typography>
                    </ImageButton>
                    <ImageButton onClick={() => handleNext('recalled with efforts')}>
                      <Image src={efforts} alt="efforts" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>With Effort</Typography>
                    </ImageButton>
                    <ImageButton onClick={() => handleNext('easily recalled')}>
                      <Image src={convenient} alt="convenient" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Easy</Typography>
                    </ImageButton>
                  </Box>
                </Box>
              )
            )}
          </FlipCardContent>
        </GradientCardWrapper>
      </FlipCard>
    </Box>
  );
}